import { InfoOutlineIcon, WarningTwoIcon } from '@chakra-ui/icons';
import {
  Box,
  Input,
  Flex,
  Text,
  useToast,
  IconButton,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import * as _ from 'lodash';
import React, { useState, useEffect, useCallback } from 'react';

import { validate as athenaValidate } from '@finley-technologies/athena-utilities';
import {
  finleyAssert,
  stringConversion as commonStringConversion,
  utils as commonUtils,
} from '@finley-technologies/common';
import {
  DataLake,
  typeguards as finleyTypeguards,
  Util,
} from '@finley-technologies/finley-types';

import { useExecuteAthenaRecurringQueryByRawLazyQueryGQLType } from '../requests/requests.gql.generated';
import { AthenaDataPreviewModal } from './AthenaDataPreviewModal';
import { AthenaRecurringDatesContainer } from './AthenaRecurringDatesContainer';
import { AthenaRecurringQueryExecuteModal } from './AthenaRecurringQueryExecuteModal';
import { BindingContainer } from './BindingContainer';
import { ColumnNamesContainer } from './ColumnNamesContainer';
import { inputWidth } from './consts';
import { LenderAthenaRecurringQueryView } from './LenderAthenaRecurringQueryView';
import { VariableContainer } from './VariableContainer';
import { AthenaQueryStringWithSchemaSidebarContainer } from 'packages/athena/ui/AthenaQueryStringWithSchemaSidebarContainer';
import {
  AddIconButton,
  DeleteIconButton,
  EditIconButton,
  SaveIconButton,
  SearchIconButton,
  XIconButton,
  CopyIconButton,
} from 'packages/common-frontend/components/IconButton';
import { Select } from 'packages/common-frontend/components/Select';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { DeleteConfirmationModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeleteArrayPropertyButton';
import { WipConfig } from 'packages/creditAgreements/ui/types';
import {
  AddQueryInputModal,
  PortfolioCovenantQueryInput,
} from 'packages/settings/ui/AddQueryInputModal';
import { PortfolioCovenantEditTab } from 'packages/settings/ui/SettingsFacilityPortfolioCovenantsSidebar';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

export const createAthenaRecurringQueryShell = (
  idx: number,
  name?: string,
  globalRecurringDateConfigFieldPlaceholder?: string[],
): CreditAgreement.Config.AthenaConfig.RecurringQuery => ({
  name: name ?? `queryName_${idx}`,
  globalRecurringDateConfigFields:
    globalRecurringDateConfigFieldPlaceholder || [],
  queryString: 'SQL QUERY GOES HERE;',
  variables: [],
  bindings: [],
  columnNames: ['placeholderColumn'],
  defaultTo: { value: '', parseString: false },
});

type AthenaRecurringQueryViewProps = {
  organizationId: string;
  recurringQuery: CreditAgreement.Config.AthenaConfig.RecurringQuery;
  existingQueries: CreditAgreement.Config.AthenaConfig.RecurringQuery[];
  globalRecurringDateConfigs: CreditAgreement.Config.GlobalRecurringDate.Config[];
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  basePropertyKey: string;
  idx: number;
  inputDateType: 'reportingDate' | 'triggerDate';
  presetVariables: CreditAgreement.Config.QueryVariable[];
  validationVariables: CreditAgreement.Config.QueryVariable[];
  variableSubTitle?: string;
  setSelectedQueryInput?: React.Dispatch<
    React.SetStateAction<PortfolioCovenantQueryInput | null>
  >; // used to navigate to a new query input, for example when a query is duplicated
  setSelectedTab?: React.Dispatch<
    React.SetStateAction<PortfolioCovenantEditTab>
  >; // used to navigate to another tab when the currently viewed input has been deleted
  selectedInputDate?: Util.FullDateStr;
};
export const AthenaRecurringQueryView = ({
  organizationId,
  recurringQuery,
  existingQueries,
  globalRecurringDateConfigs,
  wipConfig,
  fullPropertyKey,
  basePropertyKey,
  idx,
  inputDateType,
  presetVariables,
  validationVariables,
  variableSubTitle,
  setSelectedQueryInput,
  setSelectedTab,
  selectedInputDate,
}: AthenaRecurringQueryViewProps) => {
  const toast = useToast();
  const {
    onOpen: onDuplicateQueryOpen,
    isOpen: isDuplicateQueryOpen,
    onClose: onDuplicateQueryClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteConfirmationModalOpen,
    onOpen: onDeleteConfirmationModalOpen,
    onClose: onDeleteConfirmationModalClose,
  } = useDisclosure();
  const {
    isOpen: isPreviewQueryResultsDrawerOpen,
    onOpen: onPreviewQueryResultsDrawerOpen,
    onClose: onPreviewQueryResultsDrawerClose,
  } = useDisclosure();
  const queryStringEditModeDisclosure = useDisclosure();

  const [isDuplicateLoading, setIsDuplicateLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const [queryDataItems, setQueryDataItems] = useState<
    DataLake.AthenaQueryItemData[]
  >([]);
  const [isQueryOpen, setIsQueryOpen] = useState<boolean>(false);
  const [isExecuteModalOpen, setIsExecuteModalOpen] = useState<boolean>(false);
  const [queryStringState, setQueryStringState] = useState<string>(
    recurringQuery.queryString,
  );
  const [
    isQueryStringSaveLoading,
    setIsQueryStringSaveLoading,
  ] = useState<boolean>(false);

  const duplicateQuery = useCallback(
    async ({ queryKeyInput }: { queryKeyInput: string }): Promise<void> => {
      if (!wipConfig?.isMutateAllowed(basePropertyKey)) {
        return;
      }

      const wipRecurringQueries = getIn(
        wipConfig.formProps.values,
        basePropertyKey,
      );
      finleyAssert(
        finleyTypeguards.creditAgreement.isAthenaConfigRecurringQueries(
          wipRecurringQueries,
        ),
        `Retrieved non-Athena Recurring Queries structure for [key:${basePropertyKey}]`,
      );

      const copiedQuery = _.cloneDeep(recurringQuery);
      copiedQuery.name = queryKeyInput;

      const wipQueriesCopy = _.cloneDeep(wipRecurringQueries);
      wipQueriesCopy.splice(idx + 1, 0, copiedQuery);

      await wipConfig.saveAndUpdateConfigCreditAgreementWip({
        wipConfig,
        propertyKey: basePropertyKey,
        updatedProperty: wipQueriesCopy,
        setIsSaveLoading: setIsDuplicateLoading,
      });

      if (setSelectedQueryInput) {
        setSelectedQueryInput({
          queryInputType: 'multiQuery',
          queryInputKey: queryKeyInput,
        });
      }
    },
    [basePropertyKey, idx, recurringQuery, wipConfig, setSelectedQueryInput],
  );

  const deleteQuery = useCallback(async (): Promise<void> => {
    if (!wipConfig?.isMutateAllowed(basePropertyKey)) {
      return;
    }

    const wipRecurringQueries = getIn(
      wipConfig.formProps.values,
      basePropertyKey,
    );
    finleyAssert(
      finleyTypeguards.creditAgreement.isAthenaConfigRecurringQueries(
        wipRecurringQueries,
      ),
      `Retrieved non-Athena Recurring Queries structure for [key:${basePropertyKey}]`,
    );

    const newRecurringQueriesAfterDelete = wipRecurringQueries.filter(
      (_var, varIdx) => varIdx !== idx,
    );

    if (setSelectedTab && setSelectedQueryInput) {
      setSelectedTab('properties');
      setSelectedQueryInput(null);
    }

    await wipConfig.saveAndUpdateConfigCreditAgreementWip({
      wipConfig,
      propertyKey: basePropertyKey,
      updatedProperty: newRecurringQueriesAfterDelete,
      setIsSaveLoading: setIsDeleteLoading,
    });
  }, [basePropertyKey, idx, wipConfig, setSelectedTab, setSelectedQueryInput]);

  const [isNameEditOpen, setNameEditOpen] = useState<boolean>(false);
  const [nameState, setNameState] = useState<string>(recurringQuery.name);
  const changeName = (): void => {
    if (!wipConfig) {
      return;
    }

    const nameExists = existingQueries.some((q) => q.name === nameState);
    if (nameExists) {
      toast({
        ...errorToastConfig,
        title: 'Updating SQL Name Error',
        description: `"${nameState}" name is already in use`,
      });
      return;
    }

    const wipRecurringQuery = getIn(
      wipConfig.formProps.values,
      fullPropertyKey,
    );
    finleyAssert(
      finleyTypeguards.creditAgreement.isAthenaConfigRecurringQuery(
        wipRecurringQuery,
      ),
      `Retrieved non-Athena Recurring Query structure for [key:${fullPropertyKey}]`,
    );

    const recurringQueryCopy = _.cloneDeep(wipRecurringQuery);
    recurringQueryCopy.name = nameState;
    wipConfig.formProps.setFieldValue(fullPropertyKey, recurringQueryCopy);

    setNameEditOpen(false);
  };

  const [defaultValueState, setDefaultValueState] = useState<string>(
    recurringQuery.defaultTo.value,
  );
  const [defaultParseState, setDefaultParseState] = useState<boolean>(
    recurringQuery.defaultTo.parseString,
  );
  const [
    haveDefaultSettingsChanged,
    setHaveDefaultSettingsChanged,
  ] = useState<boolean>(false);

  const [
    executeAthenaRecurringQueryByRaw,
    { loading: isQueryLoading, variables: queryVariables },
  ] = useExecuteAthenaRecurringQueryByRawLazyQueryGQLType({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onCompleted: async (data) => {
      const { items, errors } = data.athenaRecurringQueryResponse;

      if (errors.length > 0) {
        toast({
          ...errorToastConfig,
          title: 'Athena Recurring Query Execute Error',
          description: errors.join(', '),
        });

        setIsQueryStringSaveLoading(false);
        return;
      }

      if (items.length === 0) {
        toast({
          title: 'Athena Recurring Query Execute Update',
          description:
            'Query returned 0 data points. Query SQL and available column names have not been updated. Choose a different Trigger Date where data exists for the query and try again.',
          status: 'info',
          position: 'top',
          duration: 5000,
          isClosable: true,
        });

        setIsQueryStringSaveLoading(false);
        return;
      }

      if (wipConfig?.isMutateAllowed(fullPropertyKey)) {
        const recurringQueryCopy = _.cloneDeep(recurringQuery);

        // save column names and query string in CA
        recurringQueryCopy.columnNames = Object.keys(items[0]);
        if (queryVariables) {
          // save if the queryString has changed
          // so that we do not unnecessarily save when just previewing
          if (recurringQueryCopy.queryString !== queryVariables.queryString) {
            recurringQueryCopy.queryString = queryVariables.queryString;
            await wipConfig.saveAndUpdateConfigCreditAgreementWip({
              wipConfig,
              propertyKey: fullPropertyKey,
              updatedProperty: recurringQueryCopy,
              setIsSaveLoading: setIsQueryStringSaveLoading,
            });
          } else {
            // if no need to save CA, then can set loading to false immediately
            setIsQueryStringSaveLoading(false);
          }
        }

        // set the query preview results and open preview drawer
        setQueryDataItems(items);
        onPreviewQueryResultsDrawerOpen();
      }
    },
    onError: (error) => {
      setIsQueryStringSaveLoading(false);

      const errorDescription = commonUtils.EnvUtils.isCustomerFacing()
        ? 'Incurred server error when executing Athena recurring query'
        : error.message;
      toast({
        ...errorToastConfig,
        title: 'Athena Recurring Query Execute Error',
        description: errorDescription,
      });
    },
  });

  useEffect(() => {
    if (!wipConfig) {
      return;
    }

    const wipRecurringQuery = getIn(
      wipConfig.formProps.values,
      fullPropertyKey,
    );
    finleyAssert(
      finleyTypeguards.creditAgreement.isAthenaConfigRecurringQuery(
        wipRecurringQuery,
      ),
      `Retrieved non-Athena Recurring Query structure for [key:${fullPropertyKey}]`,
    );

    const { value, parseString } = wipRecurringQuery.defaultTo;
    const valuesHaveChanged =
      value !== defaultValueState || parseString !== defaultParseState;
    setHaveDefaultSettingsChanged(valuesHaveChanged);
  }, [defaultParseState, defaultValueState, fullPropertyKey, wipConfig]);

  const updateDefaultSetting = (): void => {
    if (!wipConfig || !haveDefaultSettingsChanged) {
      return;
    }

    const wipRecurringQuery = getIn(
      wipConfig.formProps.values,
      fullPropertyKey,
    );
    finleyAssert(
      finleyTypeguards.creditAgreement.isAthenaConfigRecurringQuery(
        wipRecurringQuery,
      ),
      `Retrieved non-Athena Recurring Query structure for [key:${fullPropertyKey}]`,
    );

    const recurringQueryCopy = _.cloneDeep(wipRecurringQuery);
    recurringQueryCopy.defaultTo = {
      value: defaultValueState,
      parseString: defaultParseState,
    };
    wipConfig.formProps.setFieldValue(fullPropertyKey, recurringQueryCopy);
  };

  const changeQueryString = useCallback(
    async ({
      newQueryString,
      queryInputDateForColumnUpdate,
    }: {
      newQueryString: string;
      queryInputDateForColumnUpdate: Util.FullDateStr;
    }): Promise<void> => {
      setIsQueryStringSaveLoading(true);

      if (!wipConfig?.isMutateAllowed(fullPropertyKey)) {
        return;
      }

      const wipRecurringQuery = getIn(
        wipConfig.formProps.values,
        fullPropertyKey,
      );
      finleyAssert(
        finleyTypeguards.creditAgreement.isAthenaConfigRecurringQuery(
          wipRecurringQuery,
        ),
        `Retrieved non-Athena Recurring Query structure for [key:${fullPropertyKey}]`,
      );

      const recurringQueryCopy = _.cloneDeep(wipRecurringQuery);
      recurringQueryCopy.queryString = newQueryString;

      const errors = athenaValidate.validateAthenaRecurringQuery({
        athenaRecurringQuery: {
          ...recurringQueryCopy,
          variables: [
            ...validationVariables,
            ...presetVariables,
            ...recurringQueryCopy.variables,
          ],
        },
        globalRecurringDateConfigs,
      });

      if (errors.length) {
        toast({
          ...errorToastConfig,
          title: 'Updating SQL Query Error',
          description: errors.join(', '),
        });
        throw errors;
      }

      const {
        globalRecurringDateConfigFields,
        variables,
        bindings,
        name,
      } = recurringQuery;

      // If there's any overlap with provided "presetVariables", prioritize the
      // value that's set up with the modal through "supplementalVariables" and
      // filter out what already existed in "presetVariables"
      const updatedPresetVariables = presetVariables.filter((preset) => {
        return preset.name !== inputDateType;
      });

      const supplementalVariables: CreditAgreement.Config.QueryVariable[] = [
        {
          name: inputDateType,
          value: queryInputDateForColumnUpdate,
          parseNumber: true,
        },
      ];

      executeAthenaRecurringQueryByRaw({
        variables: {
          organizationId,
          globalRecurringDateConfigsString: JSON.stringify(
            globalRecurringDateConfigs,
          ),
          globalRecurringDateConfigFields,
          supplementalVariables,
          variables: [...updatedPresetVariables, ...variables],
          bindings,
          queryString: newQueryString,
          queryName: name,
        },
      });
    },
    [
      organizationId,
      wipConfig,
      fullPropertyKey,
      presetVariables,
      toast,
      validationVariables,
      executeAthenaRecurringQueryByRaw,
      inputDateType,
      recurringQuery,
      globalRecurringDateConfigs,
    ],
  );

  const onClosePreviewQueryResultsDrawer = useCallback(() => {
    onPreviewQueryResultsDrawerClose();
    setQueryDataItems([]);
  }, [onPreviewQueryResultsDrawerClose]);

  const isClientWipMode = wipConfig?.operationMode === 'client';

  if (isClientWipMode) {
    return (
      <LenderAthenaRecurringQueryView
        recurringQuery={recurringQuery}
        fullPropertyKey={fullPropertyKey}
        wipConfig={wipConfig}
        duplicateParams={{
          isDuplicateQueryOpen,
          onDuplicateQueryOpen,
          onDuplicateQueryClose,
          duplicateQuery,
          isDuplicateLoading,
        }}
        deleteParams={{
          isDeleteConfirmationModalOpen,
          onDeleteConfirmationModalOpen,
          onDeleteConfirmationModalClose,
          deleteQuery,
          isDeleteLoading,
        }}
        changeQueryString={changeQueryString}
        isQueryStringSaveLoading={isQueryStringSaveLoading || isQueryLoading}
        inputDateType="triggerDate"
        presetVariables={presetVariables}
        validationVariables={validationVariables}
        selectedInputDate={selectedInputDate}
        globalRecurringDateConfigs={globalRecurringDateConfigs}
        queryDataItems={queryDataItems}
        isPreviewQueryResultsDrawerOpen={isPreviewQueryResultsDrawerOpen}
        onClosePreviewQueryResultsDrawer={onClosePreviewQueryResultsDrawer}
      />
    );
  }

  return (
    <Box mb="2" p="3" border="1px" borderColor="gray.700">
      <Flex alignItems="center" mb={isQueryOpen ? '2' : undefined}>
        <Text fontWeight="bold" mr="2">
          Query: &quot;{recurringQuery.name}&quot;
        </Text>
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <>
            <SearchIconButton
              ariaLabel="execute query"
              label="Execute Query"
              onClick={() => setIsExecuteModalOpen(true)}
            />
            <CopyIconButton
              ariaLabel="duplicate query"
              label="Duplicate Query"
              onClick={() => onDuplicateQueryOpen()}
            />
            <DeleteIconButton
              ariaLabel="delete query"
              label="Delete Query"
              onClick={() => onDeleteConfirmationModalOpen()}
            />
            <IconButton
              aria-label={`${isQueryOpen ? 'close' : 'display'} config`}
              icon={isQueryOpen ? <FiChevronUp /> : <FiChevronDown />}
              onClick={() => setIsQueryOpen((prev) => !prev)}
              variant="ghost"
            />
            <AddQueryInputModal
              wipConfig={wipConfig}
              isOpen={isDuplicateQueryOpen}
              onClose={onDuplicateQueryClose}
              onClickAdd={duplicateQuery}
              initialQueryType="multiQuery"
              isAddLoading={isDuplicateLoading}
              mode="duplicate"
            />
            <DeleteConfirmationModal
              isOpen={isDeleteConfirmationModalOpen}
              onClose={onDeleteConfirmationModalClose}
              onClickDelete={deleteQuery}
              isLoading={isDeleteLoading}
            />
          </>
        )}
      </Flex>

      {isQueryOpen && (
        <Box>
          {/* *** Query Name Management *** */}
          <Flex
            alignItems="center"
            border="1px"
            borderColor="gray.700"
            p="3"
            mb="5"
          >
            <Text width="40" mr="3">
              Query Name:
            </Text>
            {isNameEditOpen && wipConfig?.isMutateAllowed(fullPropertyKey) ? (
              <>
                <Input
                  mr="2"
                  type="text"
                  width={inputWidth}
                  value={nameState}
                  onChange={(e) => setNameState(e.target.value)}
                />
                <SaveIconButton
                  ariaLabel="save name"
                  label="Save Name"
                  onClick={() => changeName()}
                />
                <XIconButton
                  ariaLabel="close edit"
                  label="Close Edit"
                  onClick={() => {
                    setNameState(recurringQuery.name);
                    setNameEditOpen(false);
                  }}
                />
              </>
            ) : (
              <>
                <Text width={inputWidth} px="4">
                  {recurringQuery.name}
                </Text>
                {wipConfig?.isMutateAllowed(fullPropertyKey) && (
                  <EditIconButton
                    ariaLabel="edit name"
                    label="Edit Name"
                    onClick={() => setNameEditOpen(true)}
                    ml="2"
                  />
                )}
              </>
            )}
          </Flex>

          {/* *** Global Recurring Date Management *** */}
          <Box mb="5">
            <Text fontWeight="bold" mb="24px">
              Recurring Date Configs
            </Text>
            <AthenaRecurringDatesContainer
              recurringQuery={recurringQuery}
              globalRecurringDateConfigs={globalRecurringDateConfigs}
              fullPropertyKey={fullPropertyKey}
              wipConfig={wipConfig}
            />
          </Box>

          {/* *** Variables Management *** */}
          <VariableContainer
            wipConfig={wipConfig}
            variables={recurringQuery.variables}
            subTitle={variableSubTitle}
            variablePropertyKey={`${fullPropertyKey}.variables`}
          />

          {/* *** Bindings Management *** */}
          <BindingContainer
            wipConfig={wipConfig}
            bindings={recurringQuery.bindings}
            bindingsPropertyKey={`${fullPropertyKey}.bindings`}
            presetVariables={presetVariables}
            validationVariables={validationVariables}
            query={recurringQuery}
            queryType="multiQuery"
            globalRecurringDateConfigs={globalRecurringDateConfigs}
          />

          {/* *** SQL Query Management *** */}
          <AthenaQueryStringWithSchemaSidebarContainer
            wipConfig={wipConfig}
            fullPropertyKey={`${fullPropertyKey}.queryString`}
            changeQueryString={changeQueryString}
            isQueryStringSaveLoading={isQueryStringSaveLoading}
            queryStringState={queryStringState}
            setQueryStringState={setQueryStringState}
            selectedInputDate={selectedInputDate}
            initialQueryString={recurringQuery.queryString}
            editModeDisclosure={queryStringEditModeDisclosure}
            organizationId={organizationId}
          />

          {/* *** Column Name Portion *** */}
          <ColumnNamesContainer
            wipConfig={wipConfig}
            columnNames={recurringQuery.columnNames}
            configPropertyKey={fullPropertyKey}
            executeQueryMethod={() => setIsExecuteModalOpen(true)}
          />

          {/* *** Default Setting Portion *** */}
          <Box mb="2">
            <Flex mb="2" alignItems="center">
              <Text mr="4" fontWeight="bold">
                Default Settings
              </Text>

              {wipConfig?.isMutateAllowed(fullPropertyKey) && (
                <>
                  <Tooltip label="These default settings drive what value should be utilized instead if the referenced date configs do not output their respective max count">
                    <InfoOutlineIcon />
                  </Tooltip>

                  <SaveIconButton
                    ariaLabel="save default settings"
                    label="Save Default Settings"
                    onClick={() => {
                      updateDefaultSetting();
                    }}
                    isDisabled={!haveDefaultSettingsChanged}
                    mx="2"
                  />

                  {haveDefaultSettingsChanged && (
                    <Tooltip label="Updates are not yet saved!">
                      <WarningTwoIcon />
                    </Tooltip>
                  )}
                </>
              )}
            </Flex>

            <Flex mb="3" alignItems="center">
              <Text w="52">Value:</Text>

              {wipConfig?.isMutateAllowed(fullPropertyKey) ? (
                <Input
                  type="text"
                  mr="2"
                  width="60"
                  value={defaultValueState}
                  onChange={(e) => {
                    setDefaultValueState(String(e.target.value ?? ''));
                  }}
                />
              ) : (
                <Text width={inputWidth} px="4">
                  {recurringQuery.defaultTo.value === ''
                    ? '[ empty string ]'
                    : recurringQuery.defaultTo.value}
                </Text>
              )}
            </Flex>

            <Flex mb="7" alignItems="center">
              <Text w="52">Parse String:</Text>

              <Box w="60">
                {wipConfig?.isMutateAllowed(fullPropertyKey) ? (
                  <Select
                    value={commonUtils.toInitialCase(String(defaultParseState))}
                    onChange={(val) => {
                      if (val === undefined) {
                        return;
                      }
                      const booleanVal = commonStringConversion.convertStringBoolean(
                        val,
                      );
                      setDefaultParseState(booleanVal);
                    }}
                    options={['False', 'True'].map((value) => ({
                      label: value,
                      value,
                    }))}
                  />
                ) : (
                  <Text width={inputWidth} px="4">
                    {commonUtils.toInitialCase(
                      String(recurringQuery.defaultTo.parseString),
                    )}
                  </Text>
                )}
              </Box>
            </Flex>
          </Box>
        </Box>
      )}

      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <AthenaRecurringQueryExecuteModal
          organizationId={organizationId}
          wipConfig={wipConfig}
          fullPropertyKey={fullPropertyKey}
          globalRecurringDateConfigs={globalRecurringDateConfigs}
          presetVariables={presetVariables}
          inputDateType={inputDateType}
          isOpen={isExecuteModalOpen}
          onCancel={() => setIsExecuteModalOpen(false)}
          onQuerySuccess={setQueryDataItems}
        />
      )}

      <AthenaDataPreviewModal
        queryDataItems={queryDataItems}
        onClose={() => setQueryDataItems([])}
      />
    </Box>
  );
};

type AthenaRecurringQueriesContainerProps = {
  organizationId: string;
  wipConfig?: WipConfig;
  athenaRecurringQueries: CreditAgreement.Config.AthenaConfig.RecurringQuery[];
  globalRecurringDateConfigs: CreditAgreement.Config.GlobalRecurringDate.Config[];
  basePropertyKey: string;
  inputDateType: 'reportingDate' | 'triggerDate';
  presetVariables?: CreditAgreement.Config.QueryVariable[];
  validationVariables?: CreditAgreement.Config.QueryVariable[];
  variableSubTitle?: string;
};
export const AthenaRecurringQueriesContainer = ({
  organizationId,
  wipConfig,
  athenaRecurringQueries,
  globalRecurringDateConfigs,
  basePropertyKey,
  inputDateType,
  presetVariables = [],
  validationVariables = [],
  variableSubTitle,
}: AthenaRecurringQueriesContainerProps) => {
  const addQuery = (): void => {
    if (!wipConfig) {
      return;
    }

    const wipRecurringQueries = getIn(
      wipConfig.formProps.values,
      basePropertyKey,
    );
    finleyAssert(
      finleyTypeguards.creditAgreement.isAthenaConfigRecurringQueries(
        wipRecurringQueries,
      ),
      `Retrieved non-Athena Recurring Queries structure for [key:${basePropertyKey}]`,
    );
    const recurringQueryCopy = _.cloneDeep(wipRecurringQueries);
    recurringQueryCopy.push(
      createAthenaRecurringQueryShell(recurringQueryCopy.length),
    );
    wipConfig.formProps.setFieldValue(basePropertyKey, recurringQueryCopy);
  };

  return (
    <Box mb="6">
      <Flex alignItems="center" mb="3">
        <Text fontWeight="bold" fontSize="large" mr="2">
          Athena Recurring Queries
        </Text>
        {wipConfig?.isMutateAllowed(basePropertyKey) && (
          <AddIconButton
            ariaLabel="add new recurring query"
            label="Add New Recurring Query"
            onClick={() => {
              addQuery();
            }}
          />
        )}
      </Flex>

      {athenaRecurringQueries.length > 0 ? (
        athenaRecurringQueries.map((query, idx) => (
          <AthenaRecurringQueryView
            organizationId={organizationId}
            key={`athena-recurring-query-view-${query.name}`}
            wipConfig={wipConfig}
            basePropertyKey={basePropertyKey}
            fullPropertyKey={`${basePropertyKey}[${idx}]`}
            recurringQuery={query}
            existingQueries={athenaRecurringQueries}
            globalRecurringDateConfigs={globalRecurringDateConfigs}
            idx={idx}
            inputDateType={inputDateType}
            presetVariables={presetVariables}
            validationVariables={validationVariables}
            variableSubTitle={variableSubTitle}
          />
        ))
      ) : (
        <Text p="4" border="1px" borderColor="gray.700">
          There are currently no Athena recurring queries
        </Text>
      )}
    </Box>
  );
};
